import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      overflow: 'hidden',
      maxWidth: 440,
      width: '100%',
      margin: theme.spacing(2),
      boxShadow: '0 8px 32px rgba(15, 23, 42, 0.14)',
      backgroundColor: '#fff',
    },
  },
  /** Cabeçalho claro, conteúdo centralizado (padrão de modal do sistema) */
  header: {
    textAlign: 'center',
    padding: theme.spacing(3, 3, 2),
    background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)',
  },
  headerIconWrap: {
    width: 56,
    height: 56,
    margin: '0 auto',
    borderRadius: '50%',
    border: `2px solid ${theme.palette.primary.main}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    marginBottom: theme.spacing(1.5),
  },
  headerIcon: {
    fontSize: 32,
    color: theme.palette.primary.main,
  },
  headerTitle: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.35,
  },
  body: {
    backgroundColor: '#fff',
    '&.MuiDialogContent-root': {
      padding: theme.spacing(2.5, 3, 2.5),
      textAlign: 'center',
    },
  },
  lead: {
    fontSize: '0.9375rem',
    fontWeight: 400,
    color: theme.palette.text.primary,
    lineHeight: 1.55,
  },
  subtext: {
    marginTop: theme.spacing(1.25),
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  previewBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 10,
    backgroundColor: '#f1f5f9',
    border: '1px solid #e2e8f0',
    textAlign: 'left',
  },
  previewText: {
    fontSize: '0.8125rem',
    fontStyle: 'italic',
    color: '#475569',
    wordBreak: 'break-word',
    lineHeight: 1.45,
  },
  divider: {
    backgroundColor: '#e8edf2',
  },
  footer: {
    borderTop: 'none',
    backgroundColor: '#fff',
    flexWrap: 'nowrap',
    '&&': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      width: '100%',
      boxSizing: 'border-box',
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 2, 2),
    },
  },
  footerButton: {
    flex: 'none',
    borderRadius: 8,
    padding: theme.spacing(0.5, 1.75),
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8125rem',
    minHeight: 36,
    lineHeight: 1.2,
    boxShadow: 'none',
  },
  cancelButton: {
    backgroundColor: '#E57373',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#ef5350',
      boxShadow: 'none',
    },
  },
  confirmButton: {
    backgroundColor: '#2E7D32',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#1b5e20',
      boxShadow: 'none',
    },
  },
  btnIconBadge: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.95)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(0.5),
    '& svg': {
      fontSize: 14,
    },
  },
  cancelIcon: {
    color: '#E57373',
  },
  confirmIcon: {
    color: '#2E7D32',
  },
}));

/**
 * Modal padrão do sistema para confirmar exclusão de mensagem (ex.: Atendimentos, chat interno).
 * Layout alinhado ao padrão visual de modais: cabeçalho claro + ícone centralizado + ações coral / verde.
 */
const DeleteConfirmModal = ({ open, onClose, onConfirm, messageText }) => {
  const classes = useStyles();
  const preview =
    typeof messageText === 'string' && messageText.length > 160
      ? `${messageText.substring(0, 160)}…`
      : messageText;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      aria-labelledby="delete-confirm-dialog-title"
    >
      <Box className={classes.header} component="header">
        <Box className={classes.headerIconWrap} aria-hidden>
          <HelpOutlineIcon className={classes.headerIcon} />
        </Box>
        <Typography id="delete-confirm-dialog-title" component="h2" className={classes.headerTitle}>
          {i18n.t('chatInternal.deleteModal.title')}
        </Typography>
      </Box>
      <Divider className={classes.divider} />
      <DialogContent className={classes.body}>
        <Typography className={classes.lead} component="p">
          {i18n.t('chatInternal.deleteModal.question')}
        </Typography>
        <Typography className={classes.subtext} component="p">
          {i18n.t('chatInternal.deleteModal.subtext')}
        </Typography>
        {preview ? (
          <Box className={classes.previewBox}>
            <Typography component="div" className={classes.previewText}>
              &ldquo;{preview}&rdquo;
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
      <Divider className={classes.divider} />
      <DialogActions className={classes.footer}>
        <Button
          size="small"
          onClick={onClose}
          className={`${classes.footerButton} ${classes.cancelButton}`}
          startIcon={
            <span className={classes.btnIconBadge}>
              <CloseIcon className={classes.cancelIcon} fontSize="small" />
            </span>
          }
        >
          {i18n.t('chatInternal.deleteModal.cancel')}
        </Button>
        <Button
          size="small"
          onClick={onConfirm}
          autoFocus
          className={`${classes.footerButton} ${classes.confirmButton}`}
          startIcon={
            <span className={classes.btnIconBadge}>
              <CheckIcon className={classes.confirmIcon} fontSize="small" />
            </span>
          }
        >
          {i18n.t('chatInternal.deleteModal.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmModal;
